import { PermissionDeniedState } from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '@/services/api';
import { Page } from '@/shell/Page';

const UPLOAD_GATES = [
  'Organisation and matter permissions tested against adversarial access attempts',
  'Private storage with no public files, encryption and tested backup/restore',
  'AI provider privacy, retention and contract terms assessed',
  'Logging that redacts confidential content',
  'Prompt-injection and retrieval-contamination tests',
  'Threat model, access review and privacy/legal review',
  'Documented incident response and deletion/export limits',
];

/** Documents (preview): uploads are gated behind the confidential-data admission gates. */
export function DocumentsPage() {
  const flags = useQuery({ queryKey: ['feature-flags'], queryFn: api.featureFlags });
  if (flags.data && flags.data.documentUploads !== 'unavailable') {
    return (
      <Page title="Documents" description="Client documents on your matters.">
        <div className="flex max-w-2xl flex-col gap-3 leading-relaxed text-ink-900">
          <p>
            Client documents are added to a matter, so they stay with it and are seen only by the
            people who can open that matter. Go to{' '}
            <Link to="/professional/drafting" className="font-medium text-navy-900 underline">
              Drafting
            </Link>
            , open or start a matter, and add files under Client documents, above “1. Issues and the
            law”.
          </p>
          {flags.data.documentUploads !== 'live' ? (
            <p className="font-medium">
              Testing only: use made-up documents. Do not add real client documents or anything
              confidential until the confidentiality checks are complete.
            </p>
          ) : null}
        </div>
      </Page>
    );
  }
  return (
    <Page title="Documents" description="Protected client and matter documents.">
      <PermissionDeniedState title="Document uploads are not available yet">
        <p>
          Buakei AI will not accept real client files until every confidential-data gate below has
          passed. Please do not paste confidential client material anywhere in this demo.
        </p>
      </PermissionDeniedState>
      <section aria-labelledby="gates-heading" className="flex flex-col gap-3">
        <h2 id="gates-heading" className="text-base font-semibold text-navy-900">
          Gates that must pass first
        </h2>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-900">
          {UPLOAD_GATES.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ol>
      </section>
    </Page>
  );
}

const MODULES = {
  matters: {
    title: 'Matters & clients',
    body: 'Internal client intake, conflict screening, matter opening and team permissions. Clients never receive Buakei AI accounts — there is no client portal.',
  },
  cases: {
    title: 'Case intelligence',
    body: 'Judgment discovery, case comparison and sourced precedent maps. No outcome or judge predictions.',
  },
} as const;

/** Planned post-MVP modules are described honestly, not rendered as working screens. */
export function PlannedModulePage({ module }: { module: keyof typeof MODULES }) {
  const m = MODULES[module];
  return (
    <Page title={m.title} description="Planned module — not part of the current build.">
      <div className="flex max-w-2xl flex-col gap-3 leading-relaxed text-ink-900">
        <p>{m.body}</p>
        <p className="text-sm text-muted-600">
          Planned for after the MVP (roadmap Layer 3). It will be built only after the security and
          legal-quality gates for real client data have passed.
        </p>
      </div>
    </Page>
  );
}
